// The score readout (`client-rounds-review`, **R3**; ported from
// `prototype/sketches/rounds-slice/review-screen.html`).
//
// The **G1** metric — living bees at the colony's end, queen counted, brood excluded —
// presented as **evidence about how the colony's scripts behaved**, never as a predicted
// placement. The framing is load-bearing, not editorial: a sandbox run is the player's
// colony **alone** on the full field (**G3**) against a seed they can see, while a graded
// round is contested (**G4**) on a fresh, hidden seed (**G6**). The two numbers are not
// comparable, and a player who reads the sandbox one as a forecast will feel cheated when
// the graded result differs — so the caption is required, and no label anywhere here uses
// rank or placement wording.

import type { ReviewRun } from '../../../net/rounds-review-source';
import {
  NOT_A_FORECAST_CAPTION,
  SCORE_HEADING,
  SCORE_UNIT,
  groupDigits,
  scoreStatRows,
} from '../../rounds/review-model';

export interface ScoreReadoutProps {
  run: ReviewRun;
}

export function ScoreReadout({ run }: ScoreReadoutProps) {
  return (
    <div
      data-testid="review-score"
      style={{
        position: 'absolute',
        top: 12,
        right: 12,
        width: 212,
        background: 'var(--hud-bg)',
        border: '1.5px solid var(--hud-line)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-hud)',
        padding: '10px 12px',
      }}
    >
      <h4
        style={{
          margin: '0 0 8px',
          fontWeight: 800,
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps, 0.08em)',
          color: 'var(--hud-text-dim)',
        }}
      >
        {SCORE_HEADING}
      </h4>

      <div>
        <span
          data-testid="review-score-value"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 30,
            color: 'var(--hud-text)',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}
        >
          {groupDigits(run.score)}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 12,
            color: 'var(--hud-text-dim)',
            marginLeft: 4,
          }}
        >
          {SCORE_UNIT}
        </span>
      </div>

      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {scoreStatRows(run).map((row) => (
          <div
            key={row.label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
              fontFamily: 'var(--font-display)',
              fontSize: 12,
              color: 'var(--hud-text-dim)',
            }}
          >
            <span>{row.label}</span>
            <span style={{ color: 'var(--hud-text)', fontVariantNumeric: 'tabular-nums' }}>
              {row.value}
            </span>
          </div>
        ))}
      </div>

      <p
        data-testid="review-not-a-forecast"
        style={{
          margin: '8px 0 0',
          paddingTop: 8,
          borderTop: '1px solid var(--hud-line)',
          fontSize: 10,
          lineHeight: 1.45,
          color: 'var(--hud-text-dim)',
        }}
      >
        {NOT_A_FORECAST_CAPTION}
      </p>
    </div>
  );
}
